import { Box, Switch, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import type { Region } from '../../types/entities';
import { formatIsoDateDe } from './kilnActivity';
import classes from './KilnRows.module.css';

export interface KilnRowState {
  /** Stabiler React-Key: `existing-{id}` fuer geladene, `new-{n}` fuer angehaengte Zeilen -- ueber
   * diesen Schluessel (nicht den Listenplatz) findet `PlantDetail` beim Speichern den geladenen
   * Stand einer Zeile wieder. */
  key: string;
  id?: number;
  name: string;
  activeFrom: string | null;
  activeUntil: string | null;
  allowsOwnMeasurements: boolean;
}

/** Der Ausschnitt des Werksformulars, den die Ofenzeilen brauchen. Bewusst das Formularobjekt
 * statt Werte plus Rueckrufe: die Zeilen schreiben ueber `getInputProps` direkt in dieselbe
 * Liste, aus der `PlantDetail` speichert -- und Feldfehler aus der Antwort
 * (`kilns.<i>.<feld>`) landen ohne Zutun an der richtigen Zeile. */
interface KilnRowsProps {
  form: UseFormReturnType<{
    name: string;
    region: Region | '';
    activeUntil: string | null;
    kilns: KilnRowState[];
  }>;
  /** D-11: der GESPEICHERTE Stand des Werksdatums (`plant.activeUntil`), nicht der aktuelle
   * Formularwert -- bestimmt nur den Platzhaltertext der Ofenzeilen, das Werksdatum wird nirgends
   * in eine Ofenzeile geschrieben. */
  plantActiveUntil: string | null;
}

/**
 * Ofenzeilen im Werksdetail (UI-SPEC Punkt 6, D-06/D-25). Alle Felder -- Name, Aktiv ab,
 * Stillgelegt, Messungen-Schalter -- gehoeren zum "Werk speichern"-Knopf des Werks (Orchestrierung
 * in `PlantDetail`, dort sitzt auch der Knopf "Ofen hinzufügen" neben der Ueberschrift).
 * Stilllegen heisst: ein Datum in "Stillgelegt" eintragen und speichern; ein eigener Knopf mit
 * Sofortwirkung dafuer entfaellt.
 */
export function KilnRows({ form, plantActiveUntil }: KilnRowsProps) {
  const rows = form.getValues().kilns;
  const closurePlaceholder = plantActiveUntil ? `mit dem Werk (${formatIsoDateDe(plantActiveUntil)})` : 'offen';

  if (rows.length === 0) {
    return (
      <Box>
        <Text size="sm" c="vdzGray.6">
          Kein Drehofen hinterlegt.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.header}>
        <span>Name</span>
        <span>Aktiv ab</span>
        <span>Stillgelegt</span>
        <span>Messungen</span>
      </Box>
      {rows.map((row, index) => (
        <Box key={row.key} className={classes.row}>
          <TextInput
            size="xs"
            aria-label="Ofenname"
            {...form.getInputProps(`kilns.${index}.name`)}
          />
          <DateInput
            size="xs"
            placeholder="TT.MM.JJJJ"
            valueFormat="DD.MM.YYYY"
            locale="de"
            clearable
            aria-label="Aktiv ab"
            {...form.getInputProps(`kilns.${index}.activeFrom`)}
          />
          <DateInput
            size="xs"
            placeholder={closurePlaceholder}
            valueFormat="DD.MM.YYYY"
            locale="de"
            clearable
            aria-label="Stillgelegt"
            {...form.getInputProps(`kilns.${index}.activeUntil`)}
          />
          <Switch
            size="xs"
            label="Werk darf Einzelmessungen anlegen"
            {...form.getInputProps(`kilns.${index}.allowsOwnMeasurements`, { type: 'checkbox' })}
          />
        </Box>
      ))}
    </Box>
  );
}
